import { StoreImageEnum, StoreItemStyleEnum, getCommonItem } from '@systemeio/digital-store-shared'
import { FormInput } from '@systemeio/ui'
import ItemSteps from 'modules/stores/store/components/item-steps'
import StoreImageSelector from 'modules/stores/store/components/store-image-selector'
import {
  PhysicalProductItemEditType,
  PhysicalProductItemErrorsType,
  ProductDownloadItemEditType,
  ProductDownloadItemErrorsType,
  ProductECourseItemEditType,
  ProductECourseItemErrorsType,
} from 'modules/stores/store/content/product/types/product-interface'
import { useStorePreviewContext } from 'modules/stores/store/context/store-preview-context'
import React, { Dispatch, SetStateAction, useEffect } from 'react'
import FormTextarea from 'shared/components/form-textarea'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface Props<
  StateType extends
    | ProductDownloadItemEditType
    | ProductECourseItemEditType
    | PhysicalProductItemEditType,
  ErrorsType extends
    | ProductDownloadItemErrorsType
    | ProductECourseItemErrorsType
    | PhysicalProductItemErrorsType,
> {
  tempState: StateType
  setTempState: Dispatch<SetStateAction<StateType>>
  errors: ErrorsType
  imageType: StoreImageEnum
}

const Thumbnail = <
  StateType extends
    | ProductDownloadItemEditType
    | ProductECourseItemEditType
    | PhysicalProductItemEditType,
  ErrorsType extends
    | ProductDownloadItemErrorsType
    | ProductECourseItemErrorsType
    | PhysicalProductItemErrorsType,
>({
  tempState,
  setTempState,
  errors,
  imageType,
}: Props<StateType, ErrorsType>) => {
  const { t } = useLocoTranslation()

  const { setPreview, setSingleItemMode } = useStorePreviewContext()

  useEffect(() => {
    setPreview([getCommonItem(tempState)])
    setSingleItemMode(true)
  }, [setPreview, setSingleItemMode, tempState])

  useEffect(() => {
    setTempState(prev => ({
      ...prev,
      style: StoreItemStyleEnum.callout,
    }))
  }, [])

  return (
    <ItemSteps
      steps={[
        {
          key: 'image',
          title: t('dashboard.store.image.select_header_image'),
          element: (
            <StoreImageSelector
              dataFile={tempState.thumbnail}
              imageType={imageType}
              label={t('dashboard.store.image.thumbnail')}
              onChange={dataFile =>
                setTempState(prev => ({
                  ...prev,
                  thumbnail: dataFile,
                }))
              }
            />
          ),
        },
        {
          key: 'text',
          title: t('dashboard.store.item.add_text'),
          element: (
            <div className={'flex flex-col gap-5'}>
              <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2'}>
                <FormInput
                  required
                  value={tempState.title}
                  label={t('dashboard.store.item.title')}
                  onChange={({ target: { value } }) =>
                    setTempState(prev => ({ ...prev, title: value }))
                  }
                  error={errors.title}
                />
                <FormInput
                  required
                  value={tempState.buttonTitle}
                  label={t('dashboard.store.item.button')}
                  onChange={({ target: { value } }) =>
                    setTempState(prev => ({ ...prev, buttonTitle: value }))
                  }
                  error={errors.buttonTitle}
                />
              </div>
              <FormTextarea
                required
                value={tempState.subTitle}
                label={t('dashboard.store.item.subtitle')}
                onChange={({ target: { value } }) =>
                  setTempState(prev => ({ ...prev, subTitle: value }))
                }
                error={errors.subTitle}
              />
            </div>
          ),
        },
      ]}
    />
  )
}

export default Thumbnail
